import React, { useState, useEffect } from 'react';
import { X, Maximize2, ChevronDown, Sparkles, AlertCircle, Loader2, Key, Download, Plus, ChevronRight, Lock, ExternalLink } from 'lucide-react';
import { CreativeBrief, CampaignImage, AppStep } from './types';
import { generateTwinImage, setStoredApiKey } from './services/geminiService';

declare global {
  var aistudio: {
    hasSelectedApiKey: () => Promise<boolean>;
    openSelectKey: () => Promise<void>;
  } | undefined;
}

const LIGHTING_OPTIONS = [
  "Golden Hour (Soft, Warm)", "Rembrandt (Dramatic Shadow)", "High Key (Bright, Clean)", "Low Key (Dark, Moody)", "Forest Dapple", "Overcast (Flat, Soft)", "Softbox Studio", "Natural Window Light", "Hard Flash (Candid)", "Moonlight"
];

const LENS_OPTIONS = [
  "85mm Prime (Portrait Bokeh)", "35mm (Natural, Classic)", "24mm Wide (Dramatic)", "50mm Standard", "Macro (Hyper-Detail)", "Telephoto (Wildlife Style)"
];

const CAMERA_OPTIONS = [
  "iPhone 15 Pro", "Sony A7R IV", "Sony A7S III", "Nikon Z9", "Nikon D850", "Fujifilm X-T4", "Leica M11", "Leica Q3", "RED V-RAPTOR", "ARRI Alexa Mini", "Polaroid Now+", "Disposable Camera", "Phase One XF", "Hasselblad H6D"
];

const PHOTOGRAPHIC_VIBES = [
  "Raw Documentary", "Candid Snap", "Cinematic Noir", "Moody Rainfall", "Heritage Portrait", "National Geographic Style", "Quiet Luxury", "Vintage Polaroid", "Soft Focus Editorial", "Golden Light Candid", "Gritty Urban Street", "Nordic Minimalist", "Sun-Drenched Lifestyle", "High-Contrast Monochrome", "Afternoon Haze", "Classic Studio Portrait"
];

const SHARED_LOCATIONS = [
  "Autumn Leaf Trail", "Blooming Cherry Orchard", "Rugged Mountain Ridge", "Lakeside Wooden Pier", "Wildflower Meadow", 
  "Sun-Drenched Backyard", "Golden Wheat Field", "Snowy Pine Forest", "Desert Cactus Garden", "Rustic Farmyard", 
  "Urban Dog Park", "Seaside Cliff Path", "Lavender Hillside", "Misty River Bank", "Apple Orchard", 
  "Suburban Cul-de-sac", "Botanical Garden Path", "Enchanted Redwood Forest", "Minimalist Concrete Villa", 
  "Scottish Highland Mist", "Sunny Coastal Beach", "Desert Modernist Home", "Nordic Winter Cabin", "Tokyo Side Street", 
  "Grand Library", "High-Tech Lab", "Ancient Stone Ruins", "Bioluminescent Cave", "Night Market", 
  "Arctic Tundra", "Greenhouse in Winter", "Luxury Yacht", "Parisian Balcony", "Rooftop Pool", "Wild Savannah", "Modern Art Gallery"
];

const SPECIES_CONFIG = {
  dog: {
    vibe: PHOTOGRAPHIC_VIBES,
    scene: SHARED_LOCATIONS,
    labels: { tail: "Tail Volume", ears: "Ear Posture" },
    values: { 
      tail: ["As in photo", "Plume/Fluffy", "Short", "Standard"], 
      ears: ["As in photo", "Floppy", "Alert", "Half-Folded"] 
    }
  },
  cat: {
    vibe: ["Zen Master", "Vogue Model", "Midnight Stalker", ...PHOTOGRAPHIC_VIBES],
    scene: ["Zen Garden", "Parisian Balcony", "Modern Lounge", "Moonlit Rooftops", ...SHARED_LOCATIONS],
    labels: { tail: "Tail Curl", ears: "Ear Tuft" },
    values: { 
      tail: ["As in photo", "Elegant Wrap", "Bottle-Brush", "Standard"], 
      ears: ["As in photo", "Tufted", "Small/Pointed", "Folded"] 
    }
  },
  horse: {
    vibe: ["Wild Frontier", "Battle-Hardened", "Desert Wind", ...PHOTOGRAPHIC_VIBES],
    scene: ["Foggy Moors", "Ice-Capped Peaks", "Savannah", "Coastline", ...SHARED_LOCATIONS],
    labels: { tail: "Mane Volume", ears: "Ear Focus" },
    values: { 
      tail: ["As in photo", "Flowing/Long", "Braided/Short"], 
      ears: ["As in photo", "Pricked Forward", "Relaxed"] 
    }
  },
  exotic: {
    vibe: ["Elder", "Ancient Spirit", ...PHOTOGRAPHIC_VIBES],
    scene: ["Coral Reef", "Crystal Cave", "Jungle", ...SHARED_LOCATIONS],
    labels: { tail: "Tail Span", ears: "Head Crest" },
    values: { 
      tail: ["As in photo", "Extravagant", "Minimal"], 
      ears: ["As in photo", "Prominent", "Sleek"] 
    }
  }
};

const StudioDropdown: React.FC<{ label: string; value: string; options: any[]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-2 group">
    <label className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#dffe00]/50">{label}</label>
    <div className="relative">
      <select 
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.02] border border-white/5 rounded-lg py-2.5 px-4 text-[11px] text-white/70 appearance-none focus:outline-none focus:border-[#dffe00]/30 focus:bg-white/[0.04] transition-all cursor-pointer hover:border-white/10"
      >
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-[#050505]">{opt}</option>
        ))}
      </select>
      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-white/10"><ChevronDown className="w-3 h-3" /></div>
    </div>
  </div>
);

const AnchorSlot: React.FC<{ label: string; value: string | null; onUpload: (data: string | null) => void }> = ({ label, value, onUpload }) => (
  <div className="flex flex-col gap-3 max-w-[260px] w-full mx-auto">
    <label className="text-[8px] font-bold uppercase tracking-[0.15em] text-center text-[#dffe00]/50">{label}</label>
    <div className="relative aspect-[4/5] bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden group cursor-pointer hover:border-white/20 transition-all">
      {value ? (
        <>
          <img src={value} className="w-full h-full object-cover" alt={label} />
          <button onClick={(e) => { e.preventDefault(); onUpload(null); }} className="absolute top-2 right-2 p-2 bg-black/60 rounded-full backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
        </>
      ) : (
        <label className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/10 group-hover:text-[#dffe00]/40 transition-colors cursor-pointer">
          <Plus className="w-5 h-5 stroke-[1px]" />
          <input type="file" className="hidden" accept="image/*" onChange={async e => {
             const file = e.target.files?.[0];
             if (file) {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = (ev) => onUpload(ev.target?.result as string);
             }
          }} />
        </label>
      )}
    </div>
  </div>
);

const App: React.FC = () => {
  const [step, setStep] = useState<AppStep>('landing');
  const [activeTab, setActiveTab] = useState(0);
  const [hasKey, setHasKey] = useState(false);
  const [manualKey, setManualKey] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [brief, setBrief] = useState<CreativeBrief>({
    species: 'dog', vibe: PHOTOGRAPHIC_VIBES[0], scene: SHARED_LOCATIONS[0],
    coatTexture: "Lustrous Fur", eyeColor: "Amber", earPosture: SPECIES_CONFIG.dog.values.ears[0],
    physique: "Athletic", tailVolume: SPECIES_CONFIG.dog.values.tail[0],
    expression: "Noble",
    cameraAngle: "Low Hero", cameraType: CAMERA_OPTIONS[0], lighting: LIGHTING_OPTIONS[0], lens: LENS_OPTIONS[0],
    aspectRatio: "1:1", numImages: 4, customPrompt: "", anchorImage: null, imageSize: "1K"
  });

  const [images, setImages] = useState<CampaignImage[]>([]);

  useEffect(() => {
    const initializeKey = async () => {
      // 1. Check Studio Environment (IDX)
      if (typeof window !== 'undefined' && window.aistudio) {
        const studioHasKey = await window.aistudio.hasSelectedApiKey();
        if (studioHasKey) {
          setHasKey(true);
          return;
        }
      }

      // 2. Check LocalStorage (Persisted Manual Key)
      const cachedKey = localStorage.getItem('gemini_api_key');
      if (cachedKey) {
        setStoredApiKey(cachedKey);
        setHasKey(true);
        return;
      }

      // 3. Check Environment Variable (Vite/Netlify Injection)
      if (process.env.API_KEY) {
        setStoredApiKey(process.env.API_KEY);
        setHasKey(true);
      }
    };

    initializeKey();
  }, []);

  const handleManualActivate = () => {
    if (!manualKey.trim()) return;
    const key = manualKey.trim();
    setStoredApiKey(key);
    localStorage.setItem('gemini_api_key', key);
    setHasKey(true);
  };

  const handleKeySelection = async () => {
    if (typeof window !== 'undefined' && window.aistudio) {
      await window.aistudio.openSelectKey();
      setHasKey(true);
    } else {
      // In standalone/Netlify mode, switch to the manual entry screen
      setHasKey(false);
      setManualKey('');
    }
  };

  const currentConfig = SPECIES_CONFIG[brief.species];

  const initiateProduction = async () => {
    if (!brief.anchorImage) return alert("Upload a Pet Anchor.");
    setIsGenerating(true);
    try {
      const initialImages: CampaignImage[] = Array.from({ length: brief.numImages }, (_, i) => ({
        id: `img-${i}`, themeIndex: 0, status: 'idle'
      }));
      setImages(initialImages);
      
      for (const img of initialImages) {
        setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'generating' } : p));
        try {
          const url = await generateTwinImage(brief, `${brief.vibe} in ${brief.scene}`);
          setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'completed', imageUrl: url } : p));
        } catch (e: any) {
          console.error("Image generation error:", e);
          if (e.message === "KEY_RESET_REQUIRED") {
            setHasKey(false);
            setManualKey(''); // Reset manual key if invalid
            localStorage.removeItem('gemini_api_key'); // Clear invalid key from storage
            alert("API Key connection lost or invalid.");
            break;
          }
          setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'failed', error: e.toString() } : p));
        }
      }
    } catch (e: any) {
      alert("Error generating images.");
    } finally {
      setIsGenerating(false);
    }
  };

  if (!hasKey) {
    const isStudioEnvironment = typeof window !== 'undefined' && window.aistudio;
    
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-6 text-white text-center">
        <div className="w-16 h-16 rounded-3xl border border-white/10 flex items-center justify-center mb-10 bg-white/[0.02]">
          <Lock className="w-6 h-6 text-white/30 stroke-[1.5px]" />
        </div>
        
        <div className="flex flex-col items-center gap-3 mb-12">
          <h1 className="font-headline font-semibold text-5xl tracking-[0.5px]">
            AI Twin Studio: <span className="text-[#dffe00]">Pet</span>
          </h1>
          <p className="text-white/30 text-[9px] font-medium uppercase tracking-[0.3em] mt-2">
            Connect Studio Access Key To Begin
          </p>
        </div>

        <div className="w-full max-w-[380px] space-y-4">
          {isStudioEnvironment ? (
            <div className="w-full h-14 bg-[#0a0a0a] border border-white/10 rounded-xl flex items-center px-5 text-white/20 text-sm group cursor-text" onClick={handleKeySelection}>
              <span className="opacity-50 font-medium">Connect with Project IDX</span>
              <span className="ml-[1px] w-[1.5px] h-4 bg-white/20 animate-pulse"></span>
            </div>
          ) : (
            <input 
              type="password"
              value={manualKey}
              onChange={(e) => setManualKey(e.target.value)}
              placeholder="Enter Gemini API Key"
              className="w-full h-14 bg-[#0a0a0a] border border-white/10 rounded-xl px-5 text-white text-sm focus:outline-none focus:border-[#dffe00]/50 placeholder:text-white/20 transition-all"
            />
          )}

          <button 
            onClick={isStudioEnvironment ? handleKeySelection : handleManualActivate}
            className="w-full h-14 bg-white text-black text-[11px] font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-[#e6e6e6] transition-all flex items-center justify-center"
          >
            Activate Studio
          </button>
        </div>

        <div className="w-full max-w-[380px] h-[1px] bg-white/5 mt-16 mb-8"></div>

        <a 
          href="https://aistudio.google.com/app/apikey" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-[9px] text-white/20 uppercase tracking-[0.2em] hover:text-white/60 transition-colors"
        >
          Get your free API key from Google
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    );
  }

  if (step === 'landing') {
    return (
      <div className="relative min-h-screen bg-[#050505] flex flex-col items-center justify-center">
        <div className="relative z-10 text-center animate-fade-in flex flex-col items-center">
          <h1 className="text-5xl md:text-6xl font-headline font-semibold text-white mb-2 tracking-[1px]">AI Twin <span className="text-[#dffe00]">Studio: Pet</span></h1>
          <p className="text-white/20 uppercase tracking-[0.4em] text-[8px] mb-10">High-Fidelity Companion Synthesis</p>
          <button onClick={() => setStep('setup')} className="px-10 py-4 border border-white/5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-all">Launch Studio</button>
        </div>
        
        <div className="absolute bottom-8 left-0 w-full flex justify-center">
           <button 
            onClick={handleKeySelection} 
            className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-white/40 hover:text-[#dffe00] transition-colors"
          >
            <Key className="w-3 h-3" />
            Add / Update API Key
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { label: "Identity" },
    { label: "Direction" },
    { label: "Physicality" },
    { label: "Production" }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#dffe00] selection:text-black flex flex-col">
      <nav className="fixed top-0 left-0 w-full z-50 px-8 py-5 flex justify-between items-center bg-gradient-to-b from-[#050505] to-transparent backdrop-blur-sm border-b border-white/5">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setStep('landing')}>
          <span className="font-headline font-semibold text-[11px] tracking-[1px]">AI Twin Studio: Pet</span>
        </div>
        <div className="hidden md:flex gap-10">
          {tabs.map((tab, idx) => (
            <button
              key={tab.label}
              onClick={() => setActiveTab(idx)}
              className={`flex flex-col items-center gap-1 group transition-all duration-300 ${activeTab === idx ? 'opacity-100' : 'opacity-20 hover:opacity-50'}`}
            >
              <span className="text-[10px] uppercase tracking-[0.1em] font-semibold">{tab.label}</span>
              <div className={`h-[1px] bg-[#dffe00] transition-all duration-500 ${activeTab === idx ? 'w-full' : 'w-0 group-hover:w-2'}`} />
            </button>
          ))}
        </div>
        <button className="text-[9px] font-bold uppercase tracking-widest text-white/30 hover:text-white transition-colors" onClick={() => setStep('landing')}>Exit</button>
      </nav>

      <main className="flex-1 w-full max-w-4xl mx-auto px-6 pt-32 pb-12">
        <div className="min-h-[45vh] animate-fade-in" key={activeTab}>
          {activeTab === 0 && (
            <section className="flex flex-col items-center">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-headline font-semibold mb-1 tracking-[1px]">Identity</h2>
                <p className="text-white/30 text-[8px] uppercase tracking-[0.2em]">Source Reference</p>
              </div>
              <div className="w-full flex justify-center">
                <AnchorSlot label="Source Photo" value={brief.anchorImage} onUpload={val => setBrief(p => ({ ...p, anchorImage: val }))} />
              </div>
            </section>
          )}

          {activeTab === 1 && (
            <section className="flex flex-col items-center">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-headline font-semibold mb-1 tracking-[1px]">Direction</h2>
                <p className="text-white/30 text-[8px] uppercase tracking-[0.2em]">Environment & Stylistic Mood</p>
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
                <StudioDropdown label="STYLE VIBE" value={brief.vibe} options={currentConfig.vibe} onChange={v => setBrief(p => ({ ...p, vibe: v }))} />
                <StudioDropdown label="LOCATION" value={brief.scene} options={currentConfig.scene} onChange={v => setBrief(p => ({ ...p, scene: v }))} />
                <StudioDropdown label="Camera" value={brief.cameraType} options={CAMERA_OPTIONS} onChange={v => setBrief(p => ({ ...p, cameraType: v }))} />
              </div>
            </section>
          )}

          {activeTab === 2 && (
            <section className="flex flex-col items-center">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-headline font-semibold mb-1 tracking-[1px]">Physicality</h2>
                <p className="text-white/30 text-[8px] uppercase tracking-[0.2em]">Anatomical Details</p>
              </div>
              <div className="w-full grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-6">
                <StudioDropdown label="Coat" value={brief.coatTexture} options={["Lustrous Fur", "Silky", "Textured", "Natural"]} onChange={v => setBrief(p => ({ ...p, coatTexture: v }))} />
                <StudioDropdown label="Eyes" value={brief.eyeColor} options={["Amber", "Blue", "Emerald", "Dark"]} onChange={v => setBrief(p => ({ ...p, eyeColor: v }))} />
                <StudioDropdown label={currentConfig.labels.ears} value={brief.earPosture} options={currentConfig.values.ears} onChange={v => setBrief(p => ({ ...p, earPosture: v }))} />
                <StudioDropdown label="Physique" value={brief.physique} options={["Athletic", "Petite", "Standard"]} onChange={v => setBrief(p => ({ ...p, physique: v }))} />
                <StudioDropdown label={currentConfig.labels.tail} value={brief.tailVolume} options={currentConfig.values.tail} onChange={v => setBrief(p => ({ ...p, tailVolume: v }))} />
                <StudioDropdown label="Mood" value={brief.expression} options={["Noble", "Serene", "Alert"]} onChange={v => setBrief(p => ({ ...p, expression: v }))} />
              </div>
            </section>
          )}

          {activeTab === 3 && (
            <section className="flex flex-col items-center">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-headline font-semibold mb-1 tracking-[1px]">Production</h2>
                <p className="text-white/30 text-[8px] uppercase tracking-[0.2em]">Photographic Output</p>
              </div>
              <div className="w-full grid grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                <StudioDropdown label="Lighting" value={brief.lighting} options={LIGHTING_OPTIONS} onChange={v => setBrief(p => ({ ...p, lighting: v }))} />
                <StudioDropdown label="Lens" value={brief.lens} options={LENS_OPTIONS} onChange={v => setBrief(p => ({ ...p, lens: v }))} />
                <StudioDropdown label="Angle" value={brief.cameraAngle} options={["Low Hero", "Eye Level", "Profile"]} onChange={v => setBrief(p => ({ ...p, cameraAngle: v }))} />
                <StudioDropdown 
                  label="Aspect Ratio" 
                  value={brief.aspectRatio} 
                  options={["1:1", "3:4", "4:3", "9:16", "16:9"]} 
                  onChange={v => setBrief(p => ({ ...p, aspectRatio: v as any }))} 
                />
                <StudioDropdown 
                  label="Format" 
                  value={brief.imageSize} 
                  options={["1K", "2K"]} 
                  onChange={v => setBrief(p => ({ ...p, imageSize: v as any }))} 
                />
              </div>

              <div className="w-full flex flex-col items-center">
                 <button 
                   onClick={initiateProduction} 
                   disabled={isGenerating || !brief.anchorImage} 
                   className="group relative px-14 py-4 bg-[#dffe00] text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-full hover:bg-white hover:scale-105 transition-all duration-500 disabled:opacity-10 disabled:scale-100 shadow-lg"
                 >
                   <span className="relative z-10 flex items-center gap-3">
                     {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                     {isGenerating ? "Synthesizing" : "Generate Collection"}
                   </span>
                 </button>
              </div>
            </section>
          )}
        </div>

        <div className="mt-12 pt-6 border-t border-white/5 flex justify-between items-center">
           <button 
             onClick={() => setActiveTab(p => Math.max(0, p - 1))}
             disabled={activeTab === 0}
             className="text-[8px] font-bold uppercase tracking-widest opacity-20 hover:opacity-100 disabled:opacity-0 transition-all flex items-center gap-2"
           >
             <ChevronRight className="w-3 h-3 rotate-180" /> Back
           </button>
           
           <div className="flex gap-1.5">
             {tabs.map((_, i) => (
               <div key={i} className={`w-1 h-1 rounded-full transition-all duration-500 ${activeTab === i ? 'bg-[#dffe00] w-3' : 'bg-white/5'}`} />
             ))}
           </div>

           <button 
             onClick={() => setActiveTab(p => Math.min(tabs.length - 1, p + 1))}
             disabled={activeTab === tabs.length - 1}
             className="text-[8px] font-bold uppercase tracking-widest opacity-20 hover:opacity-100 disabled:opacity-0 transition-all flex items-center gap-2"
           >
             Next <ChevronRight className="w-3 h-3" />
           </button>
        </div>

        {images.length > 0 && (
          <section className="flex flex-col items-center pt-20 animate-fade-in border-t border-white/5 mt-20">
             <div className="text-center mb-10">
               <h2 className="text-3xl font-headline font-semibold text-white mb-1 tracking-[1px]">Results</h2>
               <p className="text-white/20 text-[8px] uppercase tracking-[0.4em]">Synthesized Gallery</p>
             </div>
             <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {images.map(img => (
                  <div key={img.id} className="aspect-[3/4] bg-white/[0.01] border border-white/5 rounded-xl overflow-hidden group relative flex items-center justify-center transition-all duration-500 hover:border-[#dffe00]/20">
                    {img.status === 'completed' ? (
                      <>
                        <img src={img.imageUrl} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" alt="Master" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-3 backdrop-blur-sm">
                          <button onClick={() => setPreviewImage(img.imageUrl!)} className="p-2.5 bg-white text-black rounded-full hover:scale-110 transition-transform"><Maximize2 className="w-4 h-4" /></button>
                          <button onClick={() => { const link = document.createElement('a'); link.href = img.imageUrl!; link.download = 'pet.png'; link.click(); }} className="p-2.5 bg-[#dffe00] text-black rounded-full hover:scale-110 transition-transform"><Download className="w-4 h-4" /></button>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                         {img.status === 'failed' ? <AlertCircle className="w-4 h-4 text-red-500/20" /> : <Loader2 className="w-5 h-5 text-[#dffe00]/10 animate-spin" />}
                         <span className="text-[6px] uppercase tracking-widest text-white/10 font-bold">{img.status === 'generating' ? 'Synthesizing' : img.status === 'failed' ? 'Generation Failed' : 'Queued'}</span>
                         {img.status === 'failed' && <span className="text-[6px] text-white/10">{img.error}</span>}
                      </div>
                    )}
                  </div>
                ))}
             </div>
          </section>
        )}
      </main>

      <footer className="w-full py-8 border-t border-white/5 flex justify-center items-center">
         <button 
            onClick={handleKeySelection} 
            className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-white/40 hover:text-[#dffe00] transition-colors"
          >
            <Key className="w-3 h-3" />
            Add / Update API Key
          </button>
      </footer>

      {previewImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/98 flex items-center justify-center p-6 animate-fade-in" 
          onClick={() => setPreviewImage(null)}
        >
          <img 
            src={previewImage} 
            className="max-w-full max-h-[85vh] object-contain rounded-lg" 
            alt="Preview" 
            onClick={e => e.stopPropagation()} 
          />
        </div>
      )}
    </div>
  );
};

export default App;